/**
 * Normalize API-Football `/players/top*` payloads (stored raw on League.leagueAnalytics)
 * into flat leaderboard rows the UI renders. Returns empty array when the slice errored.
 */

import type {
  LeagueAnalyticsBoards,
  PlayerLeaderboardEntry,
  RawApiFootballPlayerRow,
  RawLeagueAnalytics,
} from '../types/leagueDetail.types';

const MAX_ENTRIES = 10;

type StatKind = 'goals' | 'assists' | 'yellow' | 'red';

function isPlayerRowArray(
  value: unknown
): value is RawApiFootballPlayerRow[] {
  return Array.isArray(value);
}

function pickPlayerName(row: RawApiFootballPlayerRow): string {
  const name = row.player?.name?.trim();
  if (name && name.length > 0) return name;
  const parts = [row.player?.firstname, row.player?.lastname]
    .filter((p): p is string => typeof p === 'string' && p.trim().length > 0);
  return parts.length > 0 ? parts.join(' ') : 'Unknown';
}

function pickStatValue(row: RawApiFootballPlayerRow, kind: StatKind): number {
  const stats = row.statistics?.[0];
  if (!stats) return 0;
  if (kind === 'goals') return Number(stats.goals?.total ?? 0) || 0;
  if (kind === 'assists') return Number(stats.goals?.assists ?? 0) || 0;
  if (kind === 'yellow') return Number(stats.cards?.yellow ?? 0) || 0;
  return Number(stats.cards?.red ?? 0) || 0;
}

function toEntries(
  rows: unknown,
  kind: StatKind
): PlayerLeaderboardEntry[] {
  if (!isPlayerRowArray(rows)) return [];
  return rows
    .slice(0, MAX_ENTRIES)
    .map((row, idx): PlayerLeaderboardEntry => {
      const teamName = row.statistics?.[0]?.team?.name?.trim() ?? '';
      const photo = row.player?.photo ?? null;
      const playerId = typeof row.player?.id === 'number' ? row.player.id : null;
      return {
        rank: idx + 1,
        playerId,
        playerName: pickPlayerName(row),
        teamName,
        photo,
        value: pickStatValue(row, kind),
      };
    })
    .filter((entry) => entry.playerName !== 'Unknown' || entry.value > 0);
}

export function normalizeLeagueAnalytics(
  raw: RawLeagueAnalytics | null
): LeagueAnalyticsBoards {
  if (!raw) {
    return { topScorers: [], topAssists: [], topYellowCards: [], topRedCards: [] };
  }
  return {
    topScorers: toEntries(raw.topScorers, 'goals'),
    topAssists: toEntries(raw.topAssists, 'assists'),
    topYellowCards: toEntries(raw.discipline?.topYellowCards, 'yellow'),
    topRedCards: toEntries(raw.discipline?.topRedCards, 'red'),
  };
}

export function hasAnyLeaderboardData(boards: LeagueAnalyticsBoards): boolean {
  return (
    boards.topScorers.length > 0 ||
    boards.topAssists.length > 0 ||
    boards.topYellowCards.length > 0 ||
    boards.topRedCards.length > 0
  );
}
